import { useCallback, useEffect, useRef, useState } from "react";
import useMobileMenuOpen from "../../hooks/useMobileMenuOpen";

const BUTTON_SIZE = 48;
const DRAG_THRESHOLD = 10;

/**
 * The floating green "i" that opens a page's guided tour.
 *
 * Every screen used to hand-roll this button, so some copies were draggable and
 * some weren't (the portfolio one was pinned). This is the one implementation:
 * drag to move, click to open, and a drag never counts as a click.
 *
 * @param {() => void} onOpen   Called on a real click (not the end of a drag).
 * @param {string}    [title]   Tooltip text.
 */
const GuideLauncher = ({ onOpen, title = "Drag to move • Click to show page tips" }) => {
  const [position, setPosition] = useState(() => ({
    // Lifted above the mobile footer icons so it never sits on top of them.
    bottom: window.innerWidth <= 768 ? 140 : 16,
    right: 3,
  }));
  const [isDragging, setIsDragging] = useState(false);

  const dragOffset = useRef({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const draggedSignificantly = useRef(false);
  const menuOpen = useMobileMenuOpen();

  const handleDragStart = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    dragOffset.current = { x: clientX - rect.left, y: clientY - rect.top };
    dragStart.current = { x: clientX, y: clientY };
    draggedSignificantly.current = false;
    setIsDragging(true);
  };

  const handleDragMove = useCallback((e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const distance = Math.hypot(clientX - dragStart.current.x, clientY - dragStart.current.y);
    if (distance > DRAG_THRESHOLD) {
      draggedSignificantly.current = true;
    }

    const right = window.innerWidth - clientX - dragOffset.current.x;
    const bottom = window.innerHeight - clientY - dragOffset.current.y;

    setPosition({
      right: Math.max(8, Math.min(right, window.innerWidth - BUTTON_SIZE)),
      bottom: Math.max(8, Math.min(bottom, window.innerHeight - BUTTON_SIZE)),
    });
  }, []);

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleDragEnd = () => setIsDragging(false);

    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDragMove, { passive: false });
    document.addEventListener("touchend", handleDragEnd);

    return () => {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", handleDragMove);
      document.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove]);

  // The tour belongs to the page underneath; over the mobile menu it would
  // point at screen parts the menu hides.
  if (menuOpen) return null;

  return (
    <button
      type="button"
      className={`fixed text-white p-0.5 rounded-full shadow-lg hover:opacity-90 z-50 select-none ${
        isDragging ? "cursor-grabbing" : "cursor-grab"
      }`}
      style={{
        bottom: `${position.bottom}px`,
        right: `${position.right}px`,
        touchAction: "none",
        backgroundColor: "#35b23c",
      }}
      onClick={() => {
        if (!draggedSignificantly.current) onOpen();
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      title={title}
      aria-label="Show page tips"
    >
      <svg width="40" height="40" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );
};

export default GuideLauncher;
